"use client";

import { Sheet, SheetContent, SheetHeader, SheetOverlay } from "@/components/rac/sheet";
import { USER_RELATION_MUTATION } from "@/gql/user-mutations";
import { useAuth } from "@/hooks/use-auth";
import { useEmailSubmission } from "@/hooks/use-email-submission";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserPreferencesStore } from "@/stores/user-preferences";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { saveGroupPreference } from "@/utils/user-preferences";
import { useMutation } from "@apollo/client";
import { Loader2, UserCheck, Users, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { EmailSubmission } from "./email-submission";
// import { EmailSubmission } from "../email-submission";
interface UserGroup {
	id: string;
	name: string;
	type: string;
	relationship: "buyer" | "seller";
	members?: Array<{ id: string }>;
}

export interface PostLoginChoiceDialogProps {
	isOpen: boolean;
	onClose: () => void;
	currentRelationship?: "buyer" | "seller";
	featureName?: string;
	description?: string;
	onComplete?: (relationship: "buyer" | "seller", groupId?: string) => void;
}

export function PostLoginChoiceDialog({
	isOpen,
	onClose,
	currentRelationship: propCurrentRelationship,
	featureName = "Choose Your Role",
	description = "Tell us how you'd like to use Settlin today",
	onComplete,
}: PostLoginChoiceDialogProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [mounted, setMounted] = useState(false);
	const [selectedRelationship, setSelectedRelationship] = useState<"buyer" | "seller" | null>(null);
	const { isEmailAvailable, submitEmail, name, email, setName, setEmail } = useEmailSubmission({});

	const { user, refetchUser } = useAuth();
	const isMobile = useIsMobile();
	const router = useRouter();

	// Prevent hydration mismatch
	useEffect(() => {
		setMounted(true);
	}, []);

	const [updateUserRelation] = useMutation(USER_RELATION_MUTATION, {
		refetchQueries: ["GetUser"],
		awaitRefetchQueries: true,
	});

	const currentRelationship =
		propCurrentRelationship || (user?.relation === "buyer" || user?.relation === "seller" ? user.relation : undefined);

	// Reset state on open/close
	useEffect(() => {
		if (isOpen) {
			// Default to current relationship
			if (currentRelationship) {
				setSelectedRelationship(currentRelationship);
			} else {
				setSelectedRelationship(null);
			}
			setIsLoading(false);
		}
	}, [isOpen, currentRelationship]);

	// Group users by relationship
	const groupsByRelationship = useMemo(() => {
		if (!user?.groups) return { buyer: [] as UserGroup[], seller: [] as UserGroup[] };

		const grouped = (user.groups as UserGroup[]).reduce(
			(acc, group) => {
				if (group.relationship === "buyer") acc.buyer.push(group);
				if (group.relationship === "seller") acc.seller.push(group);
				return acc;
			},
			{ buyer: [] as UserGroup[], seller: [] as UserGroup[] },
		);
		return grouped;
	}, [user?.groups]);

	const handleSelectRole = useCallback(
		async (newRelationship: "buyer" | "seller") => {
			if (!user || isLoading) return;

			// Submit email if user has entered email details but email is not yet available
			if (!isEmailAvailable && (name?.length || email?.length)) {
				try {
					await submitEmail();
				} catch (error) {
					// Show specific validation error message or fallback to generic message
					const errorMessage = error instanceof Error ? error.message : "Failed to save email. Please try again.";
					toast.error(errorMessage);
					return; // Don't proceed with role selection if email submission fails
				}
			}
			// If same relationship clicked and has group, just close
			if (currentRelationship === newRelationship) {
				const hasGroup = groupsByRelationship[newRelationship]?.length > 0;
				if (hasGroup) {
					onClose();
					return;
				}
			}

			// Set visual feedback immediately
			setSelectedRelationship(newRelationship);
			setIsLoading(true);

			// Small delay for visual feedback
			await new Promise(resolve => setTimeout(resolve, 300));

			try {
				const { data } = await updateUserRelation({
					variables: {
						params: {
							update: {
								relationship: newRelationship,
							},
						},
					},
				});

				if (data?.user?.update) {
					// Save group preference if a specific group was chosen or inferred
					const targetGroups = groupsByRelationship[newRelationship] || [];
					const finalGroupId = targetGroups[0]?.id;

					if (finalGroupId) {
						await saveGroupPreference(finalGroupId, newRelationship);

						// Update global store so data hooks use the correct group
						useUserPreferencesStore.getState().setSelectedGroup({
							selectedGroupId: finalGroupId,
							relationship: newRelationship,
						});
					}
					await refetchUser(data.user.update);

					toast.success(`Switched to ${newRelationship} mode`, {
						duration: 3000, // Auto-dismiss after 3 seconds
					});

					onComplete?.(newRelationship, finalGroupId);

					// Extended delay before closing and navigating
					setTimeout(() => {
						// Navigate before closing
						const targetPage = newRelationship === "seller" ? "/sell" : "/";
						router.push(targetPage);
						onClose();
					}, 2000);
				} else {
					throw new Error("Failed to update relationship");
				}
			} catch (error) {
				logger.error("Failed to switch relationship:", error);
				toast.error("Failed to update relationship. Please try again.");
				setSelectedRelationship(null); // Reset on error
			} finally {
				setIsLoading(false);
			}
		},
		[
			user,
			isLoading,
			isEmailAvailable,
			name?.length,
			email?.length,
			submitEmail,
			currentRelationship,
			groupsByRelationship,
			onClose,
			updateUserRelation,
			refetchUser,
			onComplete,
			router,
		],
	);

	if (!isOpen || !mounted) return null;

	// --- Render Content ---

	const content = (
		<div className="space-y-6">
			{/* Icon and Title */}
			<div className="text-center space-y-4">
				<div>
					<h2 className="text-xl font-bold text-gray-900 mb-2">{featureName}</h2>
					{description && <p className="text-sm text-gray-600 leading-relaxed max-w-sm mx-auto">{description}</p>}
				</div>
			</div>
			{!isEmailAvailable && <EmailSubmission name={name} email={email} setName={setName} setEmail={setEmail} />}
			{/* Content */}
			<div className="space-y-6">
				<div className="space-y-4">
					{/* Buyer Option */}
					<button
						type="button"
						onClick={() => handleSelectRole("buyer")}
						disabled={isLoading}
						className={cn(
							"w-full rounded-xl p-6 transition-all duration-300",
							"disabled:opacity-50 disabled:cursor-not-allowed",
							"flex items-center gap-4 text-left",
							selectedRelationship === "buyer"
								? "bg-primary-600 text-white shadow-lg scale-[1.02]"
								: "bg-white border-2 border-neutral-300 text-neutral-900 hover:border-primary hover:bg-neutral-50 hover:shadow-lg",
						)}
					>
						<div
							className={cn(
								"w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0",
								selectedRelationship === "buyer" ? "bg-white/20" : "bg-neutral-100",
							)}
						>
							<UserCheck
								className={cn("w-8 h-8", selectedRelationship === "buyer" ? "text-white" : "text-neutral-700")}
							/>
						</div>
						<div className="flex-1">
							<p className="text-lg font-semibold mb-1">I&apos;m a Buyer</p>
							<p className={cn("text-sm", selectedRelationship === "buyer" ? "text-white/90" : "text-neutral-600")}>
								Find and purchase properties
							</p>
						</div>
					</button>

					{/* Seller Option */}
					<button
						type="button"
						onClick={() => handleSelectRole("seller")}
						disabled={isLoading}
						className={cn(
							"w-full rounded-xl p-6 transition-all duration-300",
							"disabled:opacity-50 disabled:cursor-not-allowed",
							"flex items-center gap-4 text-left",
							selectedRelationship === "seller"
								? "bg-primary-600 text-white shadow-lg scale-[1.02] border-2 border-primary-600"
								: "bg-white border-2 border-neutral-300 text-neutral-900 hover:border-primary hover:bg-neutral-50 hover:shadow-lg",
						)}
					>
						<div
							className={cn(
								"w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0",
								selectedRelationship === "seller" ? "bg-white/20" : "bg-neutral-100",
							)}
						>
							<Users className={cn("w-8 h-8", selectedRelationship === "seller" ? "text-white" : "text-neutral-700")} />
						</div>
						<div className="flex-1">
							<p className="text-lg font-semibold mb-1">I&apos;m a Seller</p>
							<p className={cn("text-sm", selectedRelationship === "seller" ? "text-white/90" : "text-neutral-600")}>
								List and sell properties
							</p>
						</div>
					</button>
				</div>

				{isLoading && (
					<div className="flex items-center justify-center gap-2 text-gray-600">
						<Loader2 className="w-4 h-4 animate-spin" />
						<span className="text-sm">Setting up your account...</span>
					</div>
				)}
			</div>
		</div>
	);

	// --- Layout Logic ---

	if (isMobile) {
		return (
			<>
				<div
					className="fixed inset-0 z-[99998] pointer-events-auto bg-transparent"
					onClick={e => e.stopPropagation()}
				/>
				<Sheet isOpen={isOpen} onOpenChange={onClose}>
					<SheetOverlay className="z-[99999] bg-black/40" isDismissable={!isLoading}>
						{!!user?.groups?.length && (
							<div className="relative pointer-events-auto">
								<button
									type="button"
									onClick={e => {
										e.stopPropagation();
										onClose();
									}}
									className="absolute right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors z-[100001]"
									style={{ top: "calc(15vh - 20px)" }}
									aria-label="Close modal"
								>
									<X className="h-6 w-6 text-gray-800" />
								</button>
							</div>
						)}
						<SheetContent
							side="bottom"
							className="max-h-[85vh] min-h-[60vh] rounded-t-2xl bg-white flex flex-col border-0 shadow-2xl z-[100000] pointer-events-auto"
							closeButton={false}
						>
							<div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
								<SheetHeader className="relative flex-shrink-0 pb-6">
									<div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-6 md:hidden" />
									{content}
								</SheetHeader>
							</div>
						</SheetContent>
					</SheetOverlay>
				</Sheet>
			</>
		);
	}

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={isOpen => {
				if (!isOpen) onClose();
			}}
			isDismissable={!isLoading}
			className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center"
		>
			<Modal className="relative bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden outline-none">
				<Dialog className="outline-none">
					<div className="relative p-6">
						{!!user?.groups?.length && (
							<button
								type="button"
								onClick={e => {
									e.stopPropagation();
									onClose();
								}}
								className="absolute right-4 top-4 p-1 rounded-full hover:bg-gray-100 transition-colors z-10 text-gray-600"
								aria-label="Close modal"
							>
								<X className="h-5 w-5" />
							</button>
						)}
						{content}
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}
